import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminSearchField,
  AdminSortableTableHeader,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminRowAvatar,
  AdminRowMenuItem,
  AdminFixedRowActionsMenu,
  AdminRowActionsButton,
  AdminSupportingText,
  AdminAlertText,
  AdminListFilterDropdown,
  AdminRowMenuIcon,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import useClickOutside from '../../../Hooks/useClickOutside';
import { getPostsForScenarios } from '../../../data/postModerationScenarios';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';

const POST_STATUS_OPTIONS = ['Active', 'Under Review', 'Draft', 'Removed', 'Deleted'];
const POST_STATUS_FILTER_OPTIONS = [
  { value: null, label: 'All' },
  ...POST_STATUS_OPTIONS.map((status) => ({ value: status, label: status })),
];
const FLAG_STATUS_FILTER_OPTIONS = [
  { value: null, label: 'All' },
  { value: 'With Open Flags', label: 'With Open Flags' },
  { value: 'No Open Flags', label: 'No Open Flags' },
];

/** List columns — date and time (matches support / compliance queue lists). */
const formatDate = (value) => {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const datePart = d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  const timePart = d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${datePart}, ${timePart}`;
};

const displayPostType = (post) => {
  const type = (post?.tier || post?.postType || '').toString().trim();
  if (type === 'Featured' || type === 'Featured Post') return 'Featured Post';
  if (type === 'Standard' || type === 'Basic' || type === 'Post') return 'Post';
  return type || '—';
};

/** Spec-aligned Post Status (separate from Visibility). */
const displayPostStatus = (post) => {
  const s = (post?.status || '').toString().trim();
  if (s === 'Published' || s === 'Active') return 'Active';
  if (s === 'Pending' || s === 'Under Review') return 'Under Review';
  if (s === 'Draft') return 'Draft';
  if (s === 'Removed') return 'Removed';
  if (s === 'Deleted') return 'Deleted';
  return s || '—';
};

/** Open Flags: New or Active handling (not Decided). */
const isOpenFlagStatus = (status) => {
  const st = String(status || '').trim().toLowerCase();
  if (!st) return false;
  if (['decided', 'closed', 'resolved', 'cleared', 'approved'].includes(st)) return false;
  if (['new', 'active', 'under review', 'in review', 'open', 'pending', 'attached'].includes(st)) {
    return true;
  }
  return false;
};

/**
 * Any accepted open Flag hides the Post (including Low).
 * Draft / Removed / Deleted are always Hidden.
 */
const displayPostVisibility = (post) => {
  const v = (post?.visibility || '').toString().trim();
  if (v === 'Visible' || v === 'Hidden') return v;

  const status = displayPostStatus(post);
  if (status === 'Removed' || status === 'Deleted' || status === 'Draft') return 'Hidden';

  const openFlags = Array.isArray(post?.flags) ? post.flags : [];
  const hasOpen = openFlags.some((f) =>
    isOpenFlagStatus(f.handlingStatus || f.status || f.flagStatus || f.caseStatus)
  );
  if (hasOpen) return 'Hidden';
  return 'Visible';
};

const displayLocation = (post) => {
  const suburb = (post?.suburb || '').toString().trim();
  const state = (post?.region || post?.state || '').toString().trim();
  if (suburb && state) return `${suburb}, ${state}`;
  return suburb || state || '—';
};

/** Count of open Flags (New / Active cases). */
const getOpenFlagsCount = (post) => {
  if (Array.isArray(post?.flags) && post.flags.length > 0) {
    return post.flags.filter((f) =>
      isOpenFlagStatus(f.handlingStatus || f.status || f.flagStatus || f.caseStatus)
    ).length;
  }
  if (post?.openFlagsCount != null) return Number(post.openFlagsCount);
  return 0;
};

const generatePosts = () => getPostsForScenarios();

/** Approximate height of row actions menu for viewport flip / fixed position. */
const ROW_ACTIONS_MENU_HEIGHT = 52;

/** Post ID | Account | Post Title | Trade Category | Location | Flags | Post Status | Last Updated | Action */
const POSTS_TABLE_GRID =
  'minmax(96px, 1fr) minmax(140px, 1.6fr) minmax(160px, 2fr) minmax(100px, 1.1fr) minmax(120px, 1.2fr) minmax(72px, 0.7fr) minmax(100px, 1.1fr) minmax(120px, 1.2fr) minmax(56px, 0.5fr)';

const PostsSection = ({ pageTitle = 'Posts', defaultFilters = {} }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const contextBusinessId = location.state?.businessId
    ? String(location.state.businessId).trim()
    : '';
  const featuredOnly = location.state?.featured === true;

  const gridTemplateColumns = POSTS_TABLE_GRID;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [posts, setPosts] = useState([]);

  const [searchQuery, setSearchQuery] = useState(() => location.state?.listSearch || '');
  const [debouncedSearch, setDebouncedSearch] = useState(() =>
    (location.state?.listSearch || '').toString().trim()
  );

  const [filters, setFilters] = useState({
    postStatus: null,
    flagStatus: null,
    ...defaultFilters,
    ...(location.state?.listFilters || {}),
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('lastUpdated');
  const [sortOrder, setSortOrder] = useState('desc');

  const [postStatusOpen, setPostStatusOpen] = useState(false);
  const [flagStatusOpen, setFlagStatusOpen] = useState(false);

  const postStatusRef = useRef(null);
  const flagStatusRef = useRef(null);
  const [postRowActionsOpen, setPostRowActionsOpen] = useState({});
  const postRowActionsRefs = useRef({});
  const tableScrollRef = useRef(null);
  const [rowActionsMenuLayout, setRowActionsMenuLayout] = useState(null);

  const togglePostRowActions = (rowKey) => {
    setPostRowActionsOpen((prev) => {
      if (prev[rowKey]) return {};
      return { [rowKey]: true };
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(postRowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setPostRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openPostActionsRowId = useMemo(
    () => Object.keys(postRowActionsOpen).find((k) => postRowActionsOpen[k]),
    [postRowActionsOpen]
  );

  useLayoutEffect(() => {
    if (!openPostActionsRowId) {
      setRowActionsMenuLayout(null);
      return;
    }
    const el = postRowActionsRefs.current[openPostActionsRowId];
    if (!el) {
      setRowActionsMenuLayout(null);
      return;
    }
    const updateLayout = () => {
      const r = el.getBoundingClientRect();
      const gap = 4;
      const spaceBelow = window.innerHeight - r.bottom;
      const openUp = spaceBelow < ROW_ACTIONS_MENU_HEIGHT + gap;
      const topRaw = openUp ? r.top - ROW_ACTIONS_MENU_HEIGHT - gap : r.bottom + gap;
      const top = Math.min(
        Math.max(gap, topRaw),
        window.innerHeight - ROW_ACTIONS_MENU_HEIGHT - gap
      );
      setRowActionsMenuLayout({
        top,
        right: window.innerWidth - r.right,
      });
    };
    updateLayout();
    const tableEl = tableScrollRef.current;
    window.addEventListener('resize', updateLayout);
    window.addEventListener('scroll', updateLayout, true);
    tableEl?.addEventListener('scroll', updateLayout, { passive: true });
    return () => {
      window.removeEventListener('resize', updateLayout);
      window.removeEventListener('scroll', updateLayout, true);
      tableEl?.removeEventListener('scroll', updateLayout);
    };
  }, [openPostActionsRowId]);

  useClickOutside(postStatusRef, () => setPostStatusOpen(false));
  useClickOutside(flagStatusRef, () => setFlagStatusOpen(false));

  useEffect(() => {
    const handleClickOutside = (event) => {
      const filterRefs = [postStatusRef, flagStatusRef];
      const isOutsideAll = filterRefs.every((ref) => {
        const el = ref?.current;
        return !el || !el.contains(event.target);
      });

      if (isOutsideAll) {
        setPostStatusOpen(false);
        setFlagStatusOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => {
      switch (filterType) {
        case 'postStatus':
          return { ...prev, postStatus: value };
        case 'flagStatus':
          return { ...prev, flagStatus: value };
        default:
          return prev;
      }
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      setError(null);

      try {
        await new Promise((resolve) => setTimeout(resolve, 500));
        const generated = generatePosts();
        setPosts(generated);
      } catch (err) {
        setError('Failed to load posts. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const filteredPosts = useMemo(() => {
    let data = [...posts];

    if (contextBusinessId) {
      const normalizedCtx = contextBusinessId.replace(/^BUS-?/i, '').toLowerCase();
      data = data.filter((post) => {
        const bid = String(post.businessId || post.business?.businessId || post.business?.id || '')
          .replace(/^BUS-?/i, '')
          .toLowerCase();
        return bid && bid === normalizedCtx;
      });
    }

    if (featuredOnly) {
      data = data.filter((post) => displayPostType(post) === 'Featured Post');
    }

    // Search: Post ID, Post Title, Business Name
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase().trim();
      data = data.filter((post) => {
        const titleMatch = post.title?.toLowerCase().includes(query);
        const postIdMatch = post.postId?.toLowerCase().includes(query) || post.id?.toLowerCase().includes(query);
        const businessNameMatch = post.businessName?.toLowerCase().includes(query);
        return titleMatch || postIdMatch || businessNameMatch;
      });
    }

    if (filters.postStatus) {
      data = data.filter((post) => displayPostStatus(post) === filters.postStatus);
    }

    if (filters.flagStatus) {
      if (filters.flagStatus === 'No Open Flags') {
        data = data.filter((post) => getOpenFlagsCount(post) === 0);
      } else if (filters.flagStatus === 'With Open Flags') {
        data = data.filter((post) => getOpenFlagsCount(post) > 0);
      }
    }

    data.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'postStatus') {
        aValue = displayPostStatus(a).toLowerCase();
        bValue = displayPostStatus(b).toLowerCase();
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      if (sortField === 'visibility') {
        aValue = displayPostVisibility(a).toLowerCase();
        bValue = displayPostVisibility(b).toLowerCase();
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      if (sortField === 'openFlags') {
        const diff = getOpenFlagsCount(a) - getOpenFlagsCount(b);
        return sortOrder === 'asc' ? diff : -diff;
      }

      if (sortField === 'location') {
        aValue = displayLocation(a).toLowerCase();
        bValue = displayLocation(b).toLowerCase();
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      if (sortField === 'subcategory') {
        aValue = (a.subcategory || '').toString().toLowerCase();
        bValue = (b.subcategory || '').toString().toLowerCase();
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      if (sortField === 'tier' || sortField === 'postType') {
        aValue = displayPostType(a).toLowerCase();
        bValue = displayPostType(b).toLowerCase();
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      if (['postId', 'title', 'businessName', 'category'].includes(sortField)) {
        if (sortField === 'postId') {
          aValue = (a.postId || a.id || '').toString().toLowerCase();
          bValue = (b.postId || b.id || '').toString().toLowerCase();
        } else {
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      if (sortField === 'lastUpdated') {
        const diff = new Date(aValue || 0) - new Date(bValue || 0);
        return sortOrder === 'asc' ? diff : -diff;
      }

      return 0;
    });

    return data;
  }, [posts, debouncedSearch, filters, sortField, sortOrder, contextBusinessId, featuredOnly]);

  const listNavState = () => ({
    from: location.pathname,
    businessId: contextBusinessId || undefined,
    featured: featuredOnly || undefined,
    listSearch: searchQuery,
    listFilters: filters,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, debouncedSearch, contextBusinessId, featuredOnly]);

  const totalCount = filteredPosts.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
  const effectivePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    setCurrentPage((p) => Math.min(p, Math.max(1, Math.ceil(filteredPosts.length / CONFIG_LIST_PAGE_SIZE))));
  }, [filteredPosts.length]);

  const currentPagePosts = useMemo(() => {
    const start = (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE;
    return filteredPosts.slice(start, start + CONFIG_LIST_PAGE_SIZE);
  }, [filteredPosts, effectivePage]);

  const rangeStart = totalCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalCount);
  const showPagination = totalCount > CONFIG_LIST_PAGE_SIZE;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const renderRow = (post) => {
    const postId = post.postId || post.id;
    const statusLabel = displayPostStatus(post);
    const locationLabel = displayLocation(post);
    const openFlags = getOpenFlagsCount(post);
    return (
      <div
        key={postId}
        className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4 cursor-pointer"
        style={{ gridTemplateColumns }}
        onClick={() =>
          navigate(`/admin/posts/${postId}`, { state: { ...listNavState(), post } })
        }
      >
        <div className="flex items-center">
          <AdminTableBody>{post.postId || post.id || '—'}</AdminTableBody>
        </div>

        <div className="flex items-center gap-2 min-w-0">
          <AdminRowAvatar
            src={post.profilePicture || post.logo || require('../../../assets/img/User.png')}
            alt={post.businessName || ''}
            onError={(e) => {
              e.target.src = require('../../../assets/img/User.png');
            }}
          />
          <AdminTableBody emphasis className="truncate" title={post.businessName || '—'}>
            {post.businessName || '—'}
          </AdminTableBody>
        </div>

        <div className="flex items-center gap-2 min-w-0">
          <AdminRowAvatar
            src={
              post.thumbnail ||
              post.gallery?.[0]?.url ||
              post.images?.[0]?.url ||
              require('../../../assets/img/User.png')
            }
            alt={post.title || ''}
            onError={(e) => {
              e.target.src = require('../../../assets/img/User.png');
            }}
          />
          <AdminTableBody emphasis className="truncate" title={post.title || '—'}>
            {post.title || '—'}
          </AdminTableBody>
        </div>

        <div className="flex items-center min-w-0">
          <AdminTableBody className="truncate" title={post.category || '—'}>
            {post.category || '—'}
          </AdminTableBody>
        </div>

        <div className="flex items-center min-w-0">
          <AdminTableBody className="truncate" title={locationLabel}>
            {locationLabel}
          </AdminTableBody>
        </div>

        <div className="flex items-center">
          <AdminTableBody className="tabular-nums">{openFlags}</AdminTableBody>
        </div>

        <div className="flex items-center">
          {statusLabel && statusLabel !== '—' ? (
            <AdminStatusBadge status={statusLabel} label={statusLabel} />
          ) : (
            <AdminTableBody>—</AdminTableBody>
          )}
        </div>

        <div className="flex items-center min-w-0">
          <AdminTableMeta className="truncate" title={formatDate(post.lastUpdated)}>
            {formatDate(post.lastUpdated)}
          </AdminTableMeta>
        </div>

        <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
          <div
            className="relative inline-flex"
            ref={(el) => {
              postRowActionsRefs.current[postId] = el;
            }}
          >
            <AdminRowActionsButton
              onClick={(e) => {
                e.stopPropagation();
                togglePostRowActions(postId);
              }}
              title="Actions"
              aria-label="Row actions"
            >
              <AdminRowMenuIcon />
            </AdminRowActionsButton>
            {postRowActionsOpen[postId] && rowActionsMenuLayout && (
              <AdminFixedRowActionsMenu layout={rowActionsMenuLayout}>
                <AdminRowMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/admin/posts/${postId}`, { state: { ...listNavState(), post } });
                    setPostRowActionsOpen({});
                  }}
                >
                  View
                </AdminRowMenuItem>
              </AdminFixedRowActionsMenu>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>{pageTitle}</AdminPageTitle>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <AdminSearchField
              className="w-full lg:max-w-[26.25rem]"
              type="text"
              placeholder="Search by Post ID, Post Title, Business Name"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              aria-label="Search posts"
            />

            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Post Status"
                value={filters.postStatus}
                options={POST_STATUS_FILTER_OPTIONS}
                onChange={(value) => handleFilterChange('postStatus', value)}
                menuRef={postStatusRef}
                open={postStatusOpen}
                setOpen={setPostStatusOpen}
              />
              <AdminListFilterDropdown
                label="Open Flags"
                value={filters.flagStatus}
                options={FLAG_STATUS_FILTER_OPTIONS}
                onChange={(value) => handleFilterChange('flagStatus', value)}
                menuRef={flagStatusRef}
                open={flagStatusOpen}
                setOpen={setFlagStatusOpen}
              />
            </div>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, index) => (
                <div key={index} className="h-12 bg-white animate-pulse rounded"></div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-10">
              <AdminAlertText className="text-alertred mb-4 block">{error}</AdminAlertText>
            </div>
          ) : !filteredPosts.length ? (
            <div className="text-center py-10">
              <AdminSupportingText className="text-mediumgray">No posts match your filters.</AdminSupportingText>
            </div>
          ) : (
            <div ref={tableScrollRef} className="overflow-x-auto">
              <div className="min-w-full">
                <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns }}>
                {[
                  { field: 'postId', label: 'Post ID' },
                  { field: 'businessName', label: 'Account' },
                  { field: 'title', label: 'Post Title' },
                  { field: 'category', label: 'Trade Category' },
                  { field: 'location', label: 'Location' },
                  { field: 'openFlags', label: 'Flags' },
                  { field: 'postStatus', label: 'Post Status' },
                  { field: 'lastUpdated', label: 'Last Updated' },
                  { field: null, label: 'Action' },
                ].map(({ field, label }) => (
                  <div className={`flex items-center ${field ? '' : 'justify-center'}`} key={field || label}>
                    {field ? (
                      <AdminSortableTableHeader onClick={() => handleSort(field)}>{label}</AdminSortableTableHeader>
                    ) : (
                      <AdminTableHeader>{label}</AdminTableHeader>
                    )}
                  </div>
                ))}
              </div>

              {currentPagePosts.map(renderRow)}
              </div>
            </div>
          )}

        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={!loading && !error && showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default PostsSection;

